// src/components/TelegramVerificationCard.jsx
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Chip, Stack, CircularProgress } from '@mui/material';
import { FaTelegramPlane, FaCheckCircle, FaShieldAlt, FaPhone } from 'react-icons/fa';
import TelegramVerificationModal from './TelegramVerificationModal';
import { fetchTelegramStatus, getLocalTelegramData } from '../services/telegramService';

export default function TelegramVerificationCard({ username, compact = false }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentUser = username || localStorage.getItem('kick_user') || 'Usuario';

  const checkStatus = async () => {
    const cached = getLocalTelegramData(currentUser);
    if (cached) setStatus(cached);

    try {
      const data = await fetchTelegramStatus(currentUser);
      setStatus(data);
    } catch (err) {
      console.error('Error obteniendo estado de Telegram:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, [currentUser]);

  const isVerified = Boolean(status?.verified && status?.isVerified);

  if (compact) {
    return (
      <>
        <Chip
          icon={<FaTelegramPlane style={{ color: isVerified ? '#53FC18' : '#0088cc' }} />}
          label={
            isVerified
              ? `TG: ${status?.phoneNumber || 'Verificado'}`
              : 'Verificar Telegram'
          }
          onClick={() => setModalOpen(true)}
          sx={{
            background: isVerified
              ? 'rgba(83, 252, 24, 0.15)'
              : 'rgba(0, 136, 204, 0.15)',
            border: `1px solid ${isVerified ? 'rgba(83, 252, 24, 0.4)' : 'rgba(0, 136, 204, 0.4)'}`,
            color: isVerified ? '#53FC18' : '#0088cc',
            fontWeight: 700,
            cursor: 'pointer',
            px: 1,
            py: 0.5,
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'scale(1.03)',
              boxShadow: `0 0 15px ${isVerified ? 'rgba(83, 252, 24, 0.3)' : 'rgba(0, 136, 204, 0.3)'}`
            }
          }}
        />
        <TelegramVerificationModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          username={currentUser}
          onStatusUpdate={(updated) => setStatus(updated)}
        />
      </>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          background: isVerified
            ? 'linear-gradient(135deg, rgba(83, 252, 24, 0.05) 0%, rgba(15, 20, 32, 0.8) 100%)'
            : 'linear-gradient(135deg, rgba(0, 136, 204, 0.08) 0%, rgba(15, 20, 32, 0.8) 100%)',
          border: `1px solid ${isVerified ? 'rgba(83, 252, 24, 0.3)' : 'rgba(0, 136, 204, 0.3)'}`,
          backdropFilter: 'blur(10px)',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              background: isVerified
                ? 'rgba(83, 252, 24, 0.15)'
                : 'linear-gradient(135deg, #0088cc 0%, #229ED9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isVerified ? '#53FC18' : '#FFF',
              flexShrink: 0
            }}
          >
            {isVerified ? <FaCheckCircle size={26} /> : <FaTelegramPlane size={26} />}
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#FFF' }}>
                Verificación de Telegram
              </Typography>
              <Chip
                label={isVerified ? 'Verificado ✅' : 'Pendiente ⏳'}
                size="small"
                sx={{
                  bgcolor: isVerified ? 'rgba(83, 252, 24, 0.2)' : 'rgba(255, 170, 0, 0.2)',
                  color: isVerified ? '#53FC18' : '#FFAA00',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 20
                }}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
              {isVerified
                ? `Teléfono: ${status?.phoneNumber || 'Verificado'} | Telegram ID: ${status?.telegramId || '-'}`
                : 'Verifica tu número de celular y cuenta de Telegram.'}
            </Typography>
          </Box>
        </Stack>

        <Button
          variant={isVerified ? 'outlined' : 'contained'}
          onClick={() => setModalOpen(true)}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5,
            py: 1,
            borderColor: isVerified ? 'rgba(83, 252, 24, 0.5)' : '#0088cc',
            color: isVerified ? '#53FC18' : '#FFF',
            background: isVerified ? 'transparent' : 'linear-gradient(135deg, #0088cc 0%, #229ED9 100%)',
            '&:hover': {
              background: isVerified ? 'rgba(83, 252, 24, 0.1)' : '#0099e6',
              borderColor: isVerified ? '#53FC18' : '#0088cc'
            }
          }}
        >
          {isVerified ? 'Ver Detalles' : 'Verificar Ahora'}
        </Button>
      </Paper>

      <TelegramVerificationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        username={currentUser}
        onStatusUpdate={(updated) => setStatus(updated)}
      />
    </>
  );
}
